import React from 'react';
import { isEmpty, truncate } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { AccessLevel, SimpleNewsletter } from '@dev/ee-api/ee-api-models';
import { ContentLoader, EmptyState, MobileCardRowsProps, MobileTable, Paper } from '@dev/ee-components';
import { DeletePaidNewsletter, getPaidNewslettersStore } from '@/store/paidNewsletters/actions';
import { DropdownOptions } from '@/store/paidNewsletters/types';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { StatusBadge } from '@/assets/StatusBadge';
import { useAccessLevel } from '@/services/hooks/useAccessLevel';
import { usePaidNewsletterParamsFromUrl } from '@/components/PaidNewsletters/hooks/useRequestParams';

const TableContainer = styled.div`
    margin-top: 24px;

    & > div {
        padding-bottom: 2px;
    }
`;
const TitleWrapper = styled.div`
    word-break: break-all;
`;

const useTableHeaders = (): string[] => {
    const { t } = useTranslation();

    return [t('status'), t('opened'), t('clicked')];
};

const useTableRows = (): MobileCardRowsProps[] => {
    const { t } = useTranslation();
    const paidNewsletters = useSelector(getPaidNewslettersStore);
    const params = usePaidNewsletterParamsFromUrl();

    const SkeletonTemplate = {
        tableTitle: <ContentLoader mode="row" />,
        status: <ContentLoader mode="row" />,
    };

    if (paidNewsletters.loadingTemplates) {
        return new Array(params?.limit).fill(SkeletonTemplate);
    }

    return paidNewsletters?.templatesList.map((paidNewsletter: SimpleNewsletter, index): MobileCardRowsProps => ({
        tableTitle: <TitleWrapper>{truncate(paidNewsletter.Name, { length: 45 })}</TitleWrapper>,
        Status: <StatusBadge key={`status-${index}`} value={t('draft') as string} status="Draft" borderDisabled />,
        returnOnItemClick: paidNewsletter as any,
    }));
   
};

export const MobileDraftsTable = (): JSX.Element => {
    const paidNewsletters = useSelector(getPaidNewslettersStore);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const headers = useTableHeaders();
    const rows = useTableRows();
    const { hasAccessLevel } = useAccessLevel();
    const canModifyPaidNewsletters = hasAccessLevel(AccessLevel.ModifyPaidNewsletters);
    const urlParams = usePaidNewsletterParamsFromUrl();

    const options = [
        {
            label: t('delete'),
            color: '#ff0000',
            value: DropdownOptions.Delete,
        },
    ];

    const deleteDraft = (paidNewsletter): void => {
        const name = paidNewsletter?.Name;
        const dialogParams: ShowDialogParams = {
            title: `${t('delete')} ${t('paidnewsletter').toLowerCase()}`,
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => {
                dispatch(DeletePaidNewsletter([name], urlParams));
            },
            cancelLabel: t('cancel'),
            body: t('deletesinglepaidnewsletter', {name}),
        };
    
        dispatch(ShowDialog(dialogParams));
    };

    if (isEmpty(paidNewsletters?.templatesList) && !paidNewsletters?.loadingTemplates) {
        return (
            <Paper>
                <EmptyState text={t('emptystate')} />
            </Paper>
        );
    }

    return (
        <TableContainer>
            <MobileTable
                headers={headers}
                rows={rows}
                disableSimplePopover={!canModifyPaidNewsletters}
                dropdownData={options}
                onItemClick={(e, opt, paidNewsletter): void => deleteDraft(paidNewsletter)}
            />
        </TableContainer>
    );
};
